import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Settings,
  Smartphone,
  Wifi,
  Shield,
  MapPin,
  Clock,
  AlertTriangle,
  CloudOff,
  Lock,
  Key,
  BookOpen,
  Sprout,
  Factory,
  SunMedium,
  Gauge,
  BellRing,
  Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useLanguage } from '../LanguageProvider';
import { AKILLI_GECIS_HERO_PARAGRAPHS } from '../data/akilliGecisHero';
import { SHALTERON_S_PRODUCT_FEATURES, SHALTERON_S_USE_CASES } from '../data/shalteronSContent';

type Lang = 'tr' | 'en';
type Localized<T> = Record<Lang, T>;

// Shalteron kullanım alanları: küçük görsel + başlık ve açıklama
const SHALTERON_USE_CASES: Array<{
  image: string;
  title: Localized<string>;
  description: Localized<string>;
}> = [
    {
      image: '/shalteron_ka1.png',
      title: { tr: 'Sulama Sistemleri', en: 'Irrigation Systems' },
      description: {
        tr: 'Sulama motorları, vanalar ve pompaların uzaktan kontrolü. Dijital girişlerle sulama panellerinden çalışma durumu izlenir, anlık bildirim alırsınız.',
        en: 'Remote control of irrigation motors, valves and pumps. Monitor operational status from irrigation panels via digital inputs and receive instant notifications.'
      }
    },
    {
      image: '/shalteron_ka2.png',
      title: { tr: 'Sera Otomasyon', en: 'Greenhouse Automation' },
      description: {
        tr: 'Fan, havalandırma, perde, sisleme ve ısıtma sistemlerini mobil uygulama ile uzaktan kontrol edin. SHALTERON ile sera altyapıları sensör gerektirmeden hızlı ve ekonomik uzaktan yönetilebilir.',
        en: 'Remotely control fan, ventilation, curtain, misting and heating via mobile app. SHALTERON makes greenhouse infrastructures remotely manageable quickly and economically without requiring sensors.'
      }
    },
    {
      image: '/shalteron_ka3.png',
      title: { tr: 'Jeneratör Kontrolü', en: 'Generator Control' },
      description: {
        tr: 'Jeneratör çalıştırma, durdurma veya sıfırlama işlemleri röle çıkışları ile mobil uygulama üzerinden kontrol edilir. Elektrik kesintisi veya jeneratör çalışmaya başladığında anlık bildirim alırsınız.',
        en: 'Generator start, stop or reset operations are controlled via mobile application through relay outputs. You receive instant notifications during power outages or when the generator starts.'
      }
    },
    {
      image: '/shalteron_ka4.png',
      title: { tr: 'Aydınlatma Kontrolü', en: 'Lighting Control' },
      description: {
        tr: 'Depo, fabrika, sera ve açık alanlardaki aydınlatmanın uzaktan kontrolü. Kullanım saatleri ve aydınlatma durumu izlenerek gereksiz enerji tüketimi azaltılır.',
        en: 'Remote control of lighting in warehouses, factories, greenhouses and open areas. Reduce unnecessary energy consumption by monitoring usage hours and lighting conditions.'
      }
    },
    {
      image: '/shalteron_ka5.png',
      title: { tr: 'Motor Kontrol Sistemleri', en: 'Motor Control Systems' },
      description: {
        tr: 'Kontaktörler üzerinden AC/DC motorların güvenli kontrolü. Dijital girişlerle çalışma durumu (çalıştı/durdu) takip edilir, durum değişiminde anlık bildirim gönderilir.',
        en: 'Secure control of AC/DC motors via contactors. Operational status (started/stopped) is tracked through digital inputs, with instant notifications on status change.'
      }
    },
    {
      image: '/shalteron_ka6.png',
      title: { tr: 'Alarm Sistemleri', en: 'Alarm Systems' },
      description: {
        tr: 'Alarm panelleri, acil butonlar ve güvenlik sistemleri SHALTERON ile entegre edilir. Alarm durumunda mobil uygulamaya anlık bildirim gider, gerekirse uzaktan müdahale yapılabilir.',
        en: 'Alarm panels, emergency buttons and security systems integrate with SHALTERON. In case of alarm, instant notification is sent to the mobile app and remote intervention is possible if needed.'
      }
    }
  ];

// Shalteron ürün özellikleri (tablo)
const SHALTERON_PRODUCT_FEATURES: Array<{ label: Localized<string>; value: Localized<string> }> = [
  { label: { tr: 'Ürün Adı', en: 'Product Name' }, value: { tr: 'SHALTERON', en: 'SHALTERON' } },
  { label: { tr: 'Güç', en: 'Power' }, value: { tr: '12V DC / 1A', en: '12V DC / 1A' } },
  { label: { tr: 'Kontak Çıkışı', en: 'Contact Output' }, value: { tr: '6 adet (10A 250V AC / 10A 30V DC)', en: '6 units (10A 250V AC / 10A 30V DC)' } },
  { label: { tr: 'Dijital Giriş', en: 'Digital Input' }, value: { tr: '4 adet (Kuru kontaktır. Enerji verilmemelidir)', en: '4 units (Dry contact. Power should not be supplied)' } },
  { label: { tr: 'Dahili Buzzer', en: 'Internal Buzzer' }, value: { tr: 'Var', en: 'Yes' } },
  { label: { tr: 'Kutu Tipi', en: 'Box Type' }, value: { tr: 'Ray tipi plastik kutu (Duvar montajı için kulakçıklar mevcuttur)', en: 'Rail type plastic box (Mounting ears available for wall mounting)' } },
  { label: { tr: 'Kutu Ölçüleri', en: 'Box Dimensions' }, value: { tr: '160 x 105 x 60 mm', en: '160 x 105 x 60 mm' } },
  { label: { tr: 'Şebeke Desteği', en: 'Network Support' }, value: { tr: '4G Nano SIM', en: '4G Nano SIM' } }
];

// Akıllı Geçiş Sistemi kullanım alanı görselleri (Shalteron benzeri kartlar için)
const AKILLI_GECIS_USE_CASE_IMAGES = [
  '/ticket-barriers-subway-entrance.jpg',
  '/21228.jpg',
  '/chef-preparing-recipe.jpg',
  '/empty-hallway-background.jpg',
  '/young-student-learning-library.jpg',
  '/retro-theatre-scene-celebrate-world-theatre-day.jpg',
  '/abstract-blur-shopping-mall-retails-store-interior-background.jpg',
  '/ticket-barriers-subway-entrance.jpg'
];

type Specification = {
  label: Localized<string>;
  value: Localized<string>;
};

type Project = {
  title: Localized<string>;
  category: Localized<string>;
  description: Localized<string>;
  image: string;
  features: Localized<string[]>;
  specifications: Specification[];
  useCases: Localized<string[]>;
  technologies: string[];
  warnings?: Localized<string[]>;
  androidAppUrl?: string;
  detailedUseCases?: {
    title: Localized<string>;
    description: Localized<string>;
    points?: Localized<string[]>;
  }[];
};

function AgsHeroNarrative({ lang }: { lang: Lang }) {
  return (
    <div
      className="mb-8 space-y-4 text-lg text-foreground leading-relaxed"
      lang={lang === 'en' ? 'en' : 'tr'}
      style={{
        textAlign: 'justify',
        textJustify: 'inter-word',
        hyphens: 'auto'
      }}
    >
      {AKILLI_GECIS_HERO_PARAGRAPHS[lang].map((text, i) => (
        <p key={i}>{text}</p>
      ))}
    </div>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const isShalteronModel = slug === 'shalteron';
  const isShalteronSModel = slug === 'shalteron-s';
  const isShalteronFamily = isShalteronModel || isShalteronSModel;

  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr;

  const texts = {
    tr: {
      backToProducts: 'Ürünlere Dön',
      exploreMobileApp: 'Mobil Uygulamayı Keşfedin',
      features: 'Özellikler',
      technicalSpecs: 'Teknik Özellikler',
      connection: 'Bağlantı',
      location: 'Konum',
      sensors: 'Sensörler',
      temperatureHumidity: 'Sıcaklık ve Nem',
      battery: 'Batarya',
      general: 'Genel',
      packageContents: 'Kutu İçeriği',
      warningsTitle: 'Kullanımdan Önce Dikkat Edilmesi Gerekenler',
      useCases: 'Kullanım Alanları',
      productFeatures: 'Ürün Özellikleri',
      simCardReminder: 'Ürün, internet paketi olan nano SIM kart ile çalışır.',
      shalteronSlogan1: '"Uzaktan kontrol, sınırsız senaryo, anlık bildirim.',
      shalteronSlogan2: 'SHALTERON ile kontrol her zaman cebinizde."',
      agsSlogan: '"İlimera AGS ile kriz anlarında pratik ve profesyonel tam kontrol..."',
      shalteronSSlogan1: '"Uzaktan kontrol, sınırsız senaryo, anlık bildirim.',
      shalteronSSlogan2: 'SHALTERON S ile kontrol her zaman cebinizde."',
      whyShalteronSTitle: 'Neden SHALTERON S?',
      whyShalteronSLead:
        'SHALTERON S, sahadaki elektrikli sistemleri mobil uygulama üzerinden uzaktan kontrol etmenizi sağlarken, entegre sıcaklık ve nem sensörü ile ortam koşullarını sürekli izleyerek saha hakkında daha fazla görünürlük sunan gelişmiş bir otomasyon çözümüdür.',
      shalteronSHighlightsS: [
        'Mobil uygulama ile uzaktan kontrol ve izleme',
        'Entegre sıcaklık ve nem sensörü',
        'Sensör ve kontak değişimlerinde anlık bildirim',
        'İnternet altyapısına ihtiyaç duymaz',
        'Mevcut panolarla kolay entegrasyon',
        'Kurulumu hızlı, kullanımı kolaydır'
      ],
      useCasesIntroS1:
        'SHALTERON S, sera ve tarımsal otomasyon başta olmak üzere; endüstriyel tesisler, enerji sistemleri ve saha uygulamalarında elektrikli cihazların uzaktan kontrol edilmesini ve aynı zamanda ortam koşullarının izlenmesini sağlayan güçlü ve esnek bir otomasyon çözümüdür.',
      useCasesIntroS2:
        'Cihaz üzerindeki 6 röle çıkışı ve 4 dijital giriş sayesinde elektrikli cihazlar uzaktan kontrol edilirken; entegre sıcaklık ve nem sensörü ile ortam koşulları sürekli izlenir ve kritik değişimlerde anlık bildirim alınır.',
      shalteronHighlights: [
        'Mobil uygulama ile uzaktan kontrol ve izleme',
        'Anlık bildirimlerle durum takibi',
        'Mevcut panolarla kolay entegrasyon',
        'Sensörlü ve sensörsüz model seçenekleri',
        'Hızlı kurulum, kolay kullanım'
      ],
      productVideo: 'Ürün Videosu',
      productDocumentation: 'Ürün Dokümantasyonu',
      viewPdf: 'Görüntüle',
      technologies: 'Kullanılan Teknolojiler',
      packageItem1: 'SHALTERON Kontrol Cihazı',
      packageItem2: '3M Anten',
      packageItem3: '12VDC 1A Adaptör',
      packageItem4: 'Aktivasyon Kodu',
      packageItem5: 'Hızlı Kurulum Kılavuzu',
      quantity: 'adet',
      ctaTitle: 'Bu Ürünle İlgili Daha Fazla Bilgi Almak İster misiniz?',
      ctaDescription: 'Teknik detaylar, fiyat bilgisi ve kurulum süreci hakkında uzman ekibimizden bilgi alın.',
      visitWebsite: 'Frigolive Web Sitesini Ziyaret Edin',
      contactUs: 'İletişime Geçin',
      viewOtherProjects: 'Diğer Projeleri İncele',
      projectNotFound: 'Proje Bulunamadı',
      projectNotFoundDesc: 'Aradığınız proje mevcut değil.'
    },
    en: {
      backToProducts: 'Back to Products',
      exploreMobileApp: 'Explore Mobile App',
      features: 'Features',
      technicalSpecs: 'Technical Specifications',
      connection: 'Connection',
      location: 'Location',
      sensors: 'Sensors',
      temperatureHumidity: 'Temperature and Humidity',
      battery: 'Battery',
      general: 'General',
      packageContents: 'Package Contents',
      warningsTitle: 'Important Notes Before Use',
      useCases: 'Use Cases',
      productFeatures: 'Product Features',
      simCardReminder: 'The product works with a nano SIM card that has an internet package.',
      shalteronSlogan1: '"Remote control, unlimited scenarios, instant notifications.',
      shalteronSlogan2: 'Control is always in your pocket with SHALTERON."',
      agsSlogan: '"Practical, professional full control in critical moments with İlimera AGS..."',
      shalteronSSlogan1: '"Remote control, unlimited scenarios, instant notifications.',
      shalteronSSlogan2: 'With SHALTERON S, control is always in your pocket."',
      whyShalteronSTitle: 'Why SHALTERON S?',
      whyShalteronSLead:
        'SHALTERON S is an advanced automation solution that lets you control field electrical systems remotely via the mobile app while continuously monitoring ambient temperature and humidity with its integrated sensor—delivering greater visibility into site conditions.',
      shalteronSHighlightsS: [
        'Remote control and monitoring via mobile app',
        'Integrated temperature and humidity sensor',
        'Instant notifications on sensor and contact changes',
        'Works without local internet infrastructure',
        'Easy integration with existing panels',
        'Quick to install and simple to use'
      ],
      useCasesIntroS1:
        'SHALTERON S is a powerful, flexible automation solution—especially for greenhouse and agricultural automation—that enables remote control of electrical equipment while monitoring environmental conditions across industrial sites, energy systems and field deployments.',
      useCasesIntroS2:
        'Six relay outputs and four digital inputs let you switch loads remotely, while the integrated temperature and humidity sensor keeps ambient conditions under continuous watch with instant alerts on critical changes.',
      shalteronHighlights: [
        'Remote control and monitoring via mobile app',
        'Status tracking with instant notifications',
        'Easy integration with existing panels',
        'Sensor and sensorless model options',
        'Quick setup, easy to use'
      ],
      productVideo: 'Product Video',
      productDocumentation: 'Product Documentation',
      viewPdf: 'View',
      technologies: 'Technologies Used',
      packageItem1: 'SHALTERON Control Device',
      packageItem2: '3M Antenna',
      packageItem3: '12VDC 1A Adapter',
      packageItem4: 'Activation Code',
      packageItem5: 'Quick Setup Guide',
      quantity: 'unit',
      ctaTitle: 'Would You Like More Information About This Product?',
      ctaDescription: 'Get information about technical details, pricing, and installation process from our expert team.',
      visitWebsite: 'Visit Frigolive Website',
      contactUs: 'Contact Us',
      viewOtherProjects: 'View Other Projects',
      projectNotFound: 'Project Not Found',
      projectNotFoundDesc: 'The project you are looking for does not exist.'
    }
  } as const;

  const t = texts[language] ?? texts.tr;
  const frigoliveVideoUrl =
    language === 'en'
      ? 'https://www.youtube.com/embed/Dt2KN2jmFEU'
      : 'https://www.youtube.com/embed/phLuQczs9P4';

  const FRIGOLIVE_USE_CASES: Array<{
    image: string;
    title: Localized<string>;
    description: Localized<string>;
    bullets: Localized<string[]>;
    color: string;
  }> = [
      {
        title: { tr: 'Lojistik', en: 'Logistics' },
        description: {
          tr: 'Nakliye sürecinde soğuk zincir takibi',
          en: 'Cold-chain tracking during transportation'
        },
        bullets: {
          tr: ['Rota Optimizasyonu', 'Ürün Durumu Takibi', 'Teslimat Kanıtı'],
          en: ['Route Optimization', 'Product Status Tracking', 'Proof of Delivery']
        },
        image:
          'https://images.unsplash.com/photo-1547116262-a7365d627e61?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2xkJTIwY2hhaW4lMjBsb2dpc3RpY3MlMjB0cnVja3xlbnwxfHx8fDE3NTczMDk4Nzl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
        color: 'bg-[#FF5722]'
      },
      {
        title: { tr: 'Soğuk Depo', en: 'Cold Storage' },
        description: {
          tr: 'Depo ortamı izleme ve kontrolü',
          en: 'Monitoring and control of warehouse conditions'
        },
        bullets: {
          tr: ['7/24 İzleme', 'Enerji Verimliliği', 'Öngörücü Bakım'],
          en: ['24/7 Monitoring', 'Energy Efficiency', 'Predictive Maintenance']
        },
        image:
          'https://images.unsplash.com/photo-1532635042-a6f6ad4745f9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2RkJTIwc3RvcmFnZXxlbnwxfHx8fDE3NTczMDk4Nzl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
        color: 'bg-[#C2185B]'
      },
      {
        title: { tr: 'Gıda Sektörü', en: 'Food Industry' },
        description: {
          tr: 'Gıda güvenliği için sıcaklık kontrolü',
          en: 'Temperature control for food safety'
        },
        bullets: {
          tr: ['Soğuk Zincir Uyumluluğu', 'Otomatik Uyarı Sistemi', 'Otomatik Raporlama'],
          en: ['Cold Chain Compliance', 'Automatic Alert System', 'Automated Reporting']
        },
        image:
          'https://images.unsplash.com/photo-1601599967100-f16100982063?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwdHJhbnNwb3J0YXRpb24lMjByZWZyaWdlcmF0ZWR8ZW58MXx8fHwxNzU3MzA5ODgwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
        color: 'bg-[#4CAF50]'
      },
      {
        title: { tr: 'İlaç Sektörü', en: 'Pharmaceutical Industry' },
        description: {
          tr: 'İlaç depolama koşullarının kontrolü',
          en: 'Control of pharmaceutical storage conditions'
        },
        bullets: {
          tr: ['İlaç İzlenebilirliği', 'Aşı Depolama', 'Teslim Zinciri Takibi'],
          en: ['Drug Traceability', 'Vaccine Storage', 'Delivery Chain Tracking']
        },
        image:
          'https://images.unsplash.com/photo-1671108503276-1d3d5ab23a3a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMG1lZGljaW5lJTIwc3RvcmFnZXxlbnwxfHx8fDE3NTczMDk4ODB8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
        color: 'bg-[#03A9F4]'
      },
      {
        title: { tr: 'Savunma Sanayii', en: 'Defense Industry' },
        description: {
          tr: 'Askeri ekipman ve malzemelerin depolama koşullarının kontrolü',
          en: 'Control of storage conditions for military equipment and materials'
        },
        bullets: {
          tr: ['Mühimmat Takibi', 'Askeri Standart Uyumluluğu', 'Sınıflandırılmış Veri Koruma'],
          en: ['Ammunition Tracking', 'Military Standards Compliance', 'Classified Data Protection']
        },
        image: '/hovering-drone-captures-cityscape-sunset-outdoors-generated-by-ai.jpg',
        color: 'bg-[#212121]'
      }
    ];

  const projects: Record<string, Project> = {
    'frigolive': {
      title: {
        tr: 'Frigolive F-007',
        en: 'Frigolive F-007'
      },
      category: {
        tr: 'IoT & Sensörler',
        en: 'IoT & Sensors'
      },
      description: {
        tr: 'Araç ve lojistik sektörü için geliştirilmiş, GSM tabanlı canlı konum, sıcaklık, nem, ışık ve darbe verilerini izleyerek lojistik ve hassas ürün transferi süresince ürün bütünlüğünü ve güvenliğini garanti altına alın.',
        en: 'Developed for the automotive and logistics sector, ensure product integrity and safety during logistics and sensitive product transfers by monitoring real-time location, temperature, humidity, light and shock data with GSM-based system.'
      },
      image: '/frigolive-2.png',
      features: {
        tr: [
          'Canlı Sıcaklık Takibi',
          'Hücresel Bağlantı',
          'Canlı Konum Takibi',
          'Web Tabanlı Raporlama',
          'Uzun Batarya Ömrü',
          'Mobil Uygulama Desteği',
          'Su Geçirmez Tasarım',
          'Kolay Kullanım'
        ],
        en: [
          'Live Temperature Tracking',
          'Cellular Connectivity',
          'Live Location Tracking',
          'Web-Based Reporting',
          'Long Battery Life',
          'Mobile App Support',
          'Waterproof Design',
          'Easy to Use'
        ]
      },
      specifications: [
        // Bağlantı
        {
          label: { tr: 'İletişim Aralığı', en: 'Communication Interval' },
          value: { tr: '5 dakika - 24 saat (seçilebilir aralık)', en: '5 minutes - 24 hours (selectable interval)' }
        },

        // Konum
        {
          label: { tr: 'Konumlama Yöntemi', en: 'Positioning Method' },
          value: { tr: 'GPS, WiFi, Hücresel Üçgenleme', en: 'GPS, WiFi, Cellular Triangulation' }
        },
        {
          label: { tr: 'Konum Doğruluğu', en: 'Location Accuracy' },
          value: { tr: 'GPS: 10 m, WiFi: 10 m, Hücresel: 500 m', en: 'GPS: 10 m, WiFi: 10 m, Cellular: 500 m' }
        },

        // Sensörler
        {
          label: { tr: 'Sensörler', en: 'Sensors' },
          value: { tr: 'Sıcaklık, Nem, Işık, Darbe', en: 'Temperature, Humidity, Light, Shock' }
        },
        {
          label: { tr: 'Ölçüm Kapasitesi', en: 'Measurement Capacity' },
          value: { tr: '30.000 kayda kadar', en: 'Up to 30,000 records' }
        },
        {
          label: { tr: 'Kalibrasyon', en: 'Calibration' },
          value: { tr: 'ISO/IEC 17025 akredite, NIST izlenebilir', en: 'ISO/IEC 17025 accredited, NIST traceable' }
        },

        // Sıcaklık ve Nem
        {
          label: { tr: 'Çalışma Sıcaklığı Aralığı', en: 'Operating Temperature Range' },
          value: { tr: '-25°C ile +60°C', en: '-25°C to +60°C' }
        },
        {
          label: { tr: 'Nem Aralığı', en: 'Humidity Range' },
          value: { tr: '0%-100% RH', en: '0%-100% RH' }
        },
        {
          label: { tr: 'Sıcaklık Hassasiyeti', en: 'Temperature Accuracy' },
          value: { tr: '±0.1°C', en: '±0.1°C' }
        },
        {
          label: { tr: 'Nem Hassasiyeti', en: 'Humidity Accuracy' },
          value: { tr: '±2% RH', en: '±2% RH' }
        },
        {
          label: { tr: 'Sıcaklık Sürüklenmesi', en: 'Temperature Drift' },
          value: { tr: 'Yılda <0.03°C', en: '<0.03°C per year' }
        },
        {
          label: { tr: 'Nem Sürüklenmesi', en: 'Humidity Drift' },
          value: { tr: 'Yılda %0.25', en: '0.25% per year' }
        },
        {
          label: { tr: 'Düşük Sıcaklık Desteği', en: 'Low Temperature Support' },
          value: { tr: '-30°C\'a kadar çalışma için tasarlanmıştır', en: 'Designed for operation down to -30°C' }
        },

        // Batarya
        {
          label: { tr: 'Batarya Kapasitesi', en: 'Battery Capacity' },
          value: { tr: 'Li-ion 2800mAh / 10.08 Wh', en: 'Li-ion 2800mAh / 10.08 Wh' }
        },
        {
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' },
          value: { tr: '30+ Gün (60 dk iletim), 150+ Gün (24 Saat iletim)', en: '30+ Days (60 min interval), 150+ Days (24 hr interval)' }
        },

        // Genel
        {
          label: { tr: 'Boyutlar', en: 'Dimensions' },
          value: { tr: '108 x 62 x 24 mm', en: '108 x 62 x 24 mm' }
        },
        {
          label: { tr: 'Ağırlık', en: 'Weight' },
          value: { tr: '110 gram', en: '110 grams' }
        },
        {
          label: { tr: 'Koruma', en: 'Protection' },
          value: { tr: 'IP67 (su ve toz geçirmez)', en: 'IP67 (waterproof and dustproof)' }
        },
        {
          label: { tr: 'Raf Ömrü', en: 'Shelf Life' },
          value: { tr: '1 yıl', en: '1 year' }
        }
      ],
      useCases: {
        tr: [
          'Savunma Sanayii',
          'Lojistik',
          'Soğuk Depo',
          'Gıda Sektörü',
          'İlaç Sektörü'
        ],
        en: [
          'Defense Industry',
          'Logistics',
          'Cold Storage',
          'Food Industry',
          'Pharmaceutical Industry'
        ]
      },
      technologies: ['GSM/GPRS', 'Temperature Sensors', 'Cloud Platform', 'Web Dashboard']
    },
    'shalteron': {
      title: {
        tr: 'SHALTERON',
        en: 'SHALTERON'
      },
      category: {
        tr: 'Otomasyon',
        en: 'Automation'
      },
      description: {
        tr: 'SHALTERON, sahadaki elektrikli sistemlerinizi mobil uygulama üzerinden pratik, güvenilir ve uzaktan yönetmenizi sağlayan güçlü bir otomasyon çözümüdür. İnternet altyapısına ihtiyaç duymadan çalışır ve mevcut panolarla kolayca entegre olur. Sera ve tarımsal otomasyonun yanı sıra endüstriyel tesisler, enerji sistemleri ve çeşitli saha uygulamalarında kullanılabilir. 6 röle çıkışı ve 4 dijital girişi sayesinde cihazların aç/kapat kontrolü yapılırken, çalışma durumları ve kontak değişimleri izlenir; kritik anlarda anlık bildirimler alınır.',
        en: 'SHALTERON is a powerful automation solution that lets you manage your field electrical systems practically, reliably and remotely via the mobile app. It works without requiring internet infrastructure and integrates easily with existing panels. It can be used in industrial facilities, energy systems and various field applications as well as greenhouse and agricultural automation. With 6 relay outputs and 4 digital inputs, devices can be turned on/off while operating status and contact changes are monitored; instant notifications are received at critical moments.'
      },
      image: '/SHALTERON_FINAL.png',
      features: {
        tr: [
          '6 adet röle çıkışı (10A/250V)',
          '4 adet dijital input',
          'Mobil uygulama kontrolü',
          'Zamanlama ve programlama',
          'M2M SIM Kart',
          'Durum raporlama'
        ],
        en: [
          '6 relay outputs (10A/250V)',
          '4 digital inputs',
          'Mobile app control',
          'Scheduling and programming',
          'M2M SIM Card',
          'Status reporting'
        ]
      },
      specifications: [
        {
          label: { tr: 'Kontak Çıkışı', en: 'Relay Outputs' },
          value: {
            tr: '6 adet (10A 250V AC / 10A 30V DC)',
            en: '6 outputs (10A 250V AC / 10A 30V DC)',
          },
        },
        {
          label: { tr: 'Dijital Giriş', en: 'Digital Inputs' },
          value: {
            tr: '4 adet (Kuru Kontaktır. Enerji verilmemelidir)',
            en: '4 inputs (Dry contact. Do not apply power)',
          },
        },
        {
          label: { tr: 'SIM Soketi', en: 'SIM Socket' },
          value: { tr: 'Micro SIM', en: 'Micro SIM' },
        },
        {
          label: { tr: 'Şebeke Desteği', en: 'Network Support' },
          value: { tr: '4G', en: '4G' },
        },
      ],
      useCases: {
        tr: [
          'Bahçe sulama sistemleri',
          'Sera otomasyon',
          'Endüstriyel ekipman kontrolü',
          'Aydınlatma kontrolü',
          'Motor kontrol sistemleri',
          'Alarm sistemleri'
        ],
        en: [
          'Garden irrigation systems',
          'Greenhouse automation',
          'Industrial equipment control',
          'Lighting control',
          'Motor control systems',
          'Alarm systems'
        ]
      },
      technologies: ['GSM/GPRS', 'IoT', 'Mobile App', 'SMS Gateway', 'M2M SIM Card'],
      warnings: {
        tr: [
          '10 Amperden yüksek güçler için kontaktör kullanın.',
          'Okuma girişlerine 12V\'dan fazla enerji vermeyin!',
          'Cihaz girişleri kuru kontaktır. Enerji girişi yapmayın!',
          'Cihaz kurulumu için tecrübeli bir teknik servisten destek alın.'
        ],
        en: [
          'Use a contactor for powers higher than 10 Amps.',
          'Do not apply more than 12V to the input terminals!',
          'Device inputs are dry contacts. Do not apply power!',
          'Get support from an experienced technical service for device installation.'
        ]
      }
    },
    'shalteron-s': {
      title: {
        tr: 'SHALTERON S',
        en: 'SHALTERON S'
      },
      category: {
        tr: 'Otomasyon',
        en: 'Automation'
      },
      description: {
        tr:
          'SHALTERON S, sahadaki elektrikli sistemleri mobil uygulama üzerinden uzaktan kontrol etmenizi sağlarken, entegre sıcaklık ve nem sensörü ile ortam koşullarını sürekli izleyerek saha hakkında daha fazla görünürlük sunan gelişmiş bir otomasyon çözümüdür. 6 röle çıkışı ve 4 dijital giriş ile cihazlar kontrol edilir, sensör ve kontak değişimlerinde anlık bildirim alınır; internet altyapısına ihtiyaç duymadan çalışır ve mevcut panolarla kolayca entegre olur.',
        en:
          'SHALTERON S is an advanced automation device that controls field electrical systems remotely via the mobile app while continuously monitoring ambient temperature and humidity with a built-in sensor for greater site visibility. Six relay outputs and four digital inputs switch loads and track contacts with instant alerts on sensor or contact changes. It operates without local internet infrastructure and integrates easily with existing panels.'
      },
      image: '/SHALTERON_FINAL.png',
      features: {
        tr: [
          '6 adet röle çıkışı (10A/250V)',
          '4 adet dijital input',
          'Entegre sıcaklık ve nem sensörü',
          'Mobil uygulama kontrolü',
          'Sensör ve kontak bildirimleri',
          'M2M SIM Kart'
        ],
        en: [
          '6 relay outputs (10A/250V)',
          '4 digital inputs',
          'Integrated temperature & humidity sensor',
          'Mobile app control',
          'Sensor and contact notifications',
          'M2M SIM card'
        ]
      },
      specifications: [
        {
          label: { tr: 'Kontak Çıkışı', en: 'Relay Outputs' },
          value: {
            tr: '6 adet (10A 250V AC / 10A 30V DC)',
            en: '6 outputs (10A 250V AC / 10A 30V DC)'
          }
        },
        {
          label: { tr: 'Dijital Giriş', en: 'Digital Inputs' },
          value: {
            tr: '4 adet (Kuru Kontaktır. Enerji verilmemelidir)',
            en: '4 inputs (Dry contact. Do not apply power)'
          }
        },
        {
          label: { tr: 'SIM Soketi', en: 'SIM Socket' },
          value: { tr: 'Micro SIM', en: 'Micro SIM' }
        },
        {
          label: { tr: 'Şebeke Desteği', en: 'Network Support' },
          value: { tr: '4G', en: '4G' }
        }
      ],
      useCases: {
        tr: [
          'Bahçe ve tarımsal sulama',
          'Sera otomasyonu',
          'Jeneratör ve enerji',
          'Endüstriyel ekipman',
          'Motor ve pompa',
          'Aydınlatma',
          'Saha yönetimi',
          'Alarm sistemleri'
        ],
        en: [
          'Garden & farm irrigation',
          'Greenhouse automation',
          'Generator & energy',
          'Industrial equipment',
          'Motor & pump',
          'Lighting',
          'Field management',
          'Alarm systems'
        ]
      },
      technologies: ['GSM/GPRS', 'IoT', 'Mobile App', 'SMS Gateway', 'M2M SIM Card', 'Environmental sensing'],
      warnings: {
        tr: [
          '10 Amperden yüksek güçler için kontaktör kullanın.',
          'Okuma girişlerine 12V\'dan fazla enerji vermeyin!',
          'Cihaz girişleri kuru kontaktır. Enerji girişi yapmayın!',
          'Cihaz kurulumu için tecrübeli bir teknik servisten destek alın.'
        ],
        en: [
          'Use a contactor for powers higher than 10 Amps.',
          'Do not apply more than 12V to the input terminals!',
          'Device inputs are dry contacts. Do not apply power!',
          'Get support from an experienced technical service for device installation.'
        ]
      }
    },
    'akilli-gecis-sistemi': {
      title: {
        tr: 'Akıllı Geçiş Sistemi',
        en: 'Smart Access System'
      },
      category: {
        tr: 'Güvenlik Sistemleri',
        en: 'Security Systems'
      },
      description: {
        tr: 'İlimera AGS; kurumsal tesisler, üniversite kampüsleri ve yoğun geçişli alanlar için kartlı geçiş, mobil erişim ve dinamik QR doğrulamayı tek platformda toplayan ölçeklenebilir bir erişim kontrol çözümüdür.',
        en: 'İlimera AGS is a scalable access platform for corporate sites, campuses and high-traffic areas-unifying card access, mobile credentials and dynamic QR verification.'
      },
      image: '/kgs_1.png',
      features: {
        tr: [
          'Dinamik QR kod ile giriş',
          'RFID kart desteği',
          'Mobil uygulama entegrasyonu',
          'Yoklama sistemi',
          'Detaylı raporlama',
          'Zaman dilimi kontrolü'
        ],
        en: [
          'Dynamic QR code entry',
          'RFID card support',
          'Mobile app integration',
          'Attendance system',
          'Detailed reporting',
          'Time zone control'
        ]
      },
      specifications: [
        {
          label: { tr: 'Kullanıcı Kapasitesi', en: 'User Capacity' },
          value: { tr: '20.000 kişi', en: '20,000 people' }
        },
        {
          label: { tr: 'Kayıt Kapasitesi', en: 'Record Capacity' },
          value: { tr: '100.000 kayıt', en: '100,000 records' }
        },
        {
          label: { tr: 'Tanımlama Yöntemleri', en: 'Identification Methods' },
          value: { tr: 'Proximity kart, Mifare kart, Mobil barkod', en: 'Proximity card, Mifare card, Mobile barcode' }
        },
        {
          label: { tr: 'Kart Tipi', en: 'Card Type' },
          value: { tr: 'RFID 13.56MHz, QR Code', en: 'RFID 13.56MHz, QR Code' }
        },
        {
          label: { tr: 'Okuma Mesafesi', en: 'Reading Distance' },
          value: { tr: '5-10 cm (kart) , barkod okuyucu destekli', en: '5-10 cm (card), barcode reader supported' }
        },
        {
          label: { tr: 'Ekran', en: 'Display' },
          value: { tr: '2.4" Renkli TFT Ekran', en: '2.4" Color TFT Display' }
        },
        {
          label: { tr: 'Bağlantı', en: 'Connection' },
          value: { tr: 'Ethernet / WiFi / RS-485 / USB', en: 'Ethernet / WiFi / RS-485 / USB' }
        },
        {
          label: { tr: 'Güç', en: 'Power' },
          value: { tr: '12V DC 500mA', en: '12V DC 500mA' }
        },
        {
          label: { tr: 'Boyutlar', en: 'Dimensions' },
          value: { tr: '200x150x40 mm', en: '200x150x40 mm' }
        },
        {
          label: { tr: 'Ek Fonksiyonlar', en: 'Additional Functions' },
          value: { tr: 'Anti-pass Back, Zaman Dilimi Tanımlama, Uyku Modu', en: 'Anti-pass Back, Time Zone Definition, Sleep Mode' }
        }
      ],
      useCases: {
        tr: [
          'Okul Giriş Kontrolü',
          'Ofis Personel Takibi',
          'Fabrika Vardiya Kontrolü',
          'Etkinlik Katılımcı Takibi',
          'Kütüphane Geçiş Sistemi',
          'Hastane Ziyaretçi Sistemi'
        ],
        en: [
          'School Access Control',
          'Office Personnel Tracking',
          'Factory Shift Control',
          'Event Participant Tracking',
          'Library Access System',
          'Hospital Visitor System'
        ]
      },
      detailedUseCases: [
        {
          title: {
            tr: 'Turnike Geçiş Sistemleri',
            en: 'Turnstile Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar. Kart veya mobil barkod okutulduğunda turnike otomatik açılır, yoğun saatlerde bile hızlı ve düzenli geçiş imkânı sunar. Tüm geçişler anlık olarak sisteme kaydedilir ve istenildiğinde detaylı rapor alınabilir. Bu sayede kurumlarda güvenlik ve devam kontrolü kolayca sağlanır.',
            en: 'İlimera PDKS device ensures safe and controlled passage of personnel and students through turnstiles. The turnstile opens automatically when a card or mobile barcode is scanned, providing fast and orderly passage even during busy hours. All passages are instantly recorded in the system and detailed reports can be obtained when needed. This way, security and attendance control in institutions is easily ensured.'
          },
          points: {
            tr: [
              'Personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar.',
              'Kart veya mobil QR kod ile geçiş imkânı sunar.',
              'Yoğun saatlerde bile hızlı ve düzenli geçiş sağlar.',
              'Tüm geçişler anlık olarak sisteme kaydedilir.',
              'Yetkisiz girişler engellenir.',
              'Detaylı rapor alınabilir.',
              'Kurumlarda güvenlik ve devam kontrolünü kolaylaştırır.'
            ],
            en: [
              'Ensures secure and controlled passage for staff and students through turnstiles.',
              'Provides access using cards or mobile QR codes.',
              'Ensures fast and orderly passage even during peak hours.',
              'All passages are instantly recorded in the system.',
              'Unauthorized entries are prevented.',
              'Detailed reports can be obtained.',
              'Facilitates security and attendance control in institutions.'
            ]
          }
        },
        {
          title: {
            tr: 'Kapı Geçiş Sistemleri',
            en: 'Door Access Systems'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar. Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile kolayca giriş yapabilir. Yetkisiz erişim denemeleri ise cihaz tarafından engellenir; sesli ve görsel uyarılar devreye girer ve sistem yöneticisine bildirim gönderilir. Tüm giriş-çıkışlar anlık kaydedilir ve detaylı raporlar oluşturularak kurum güvenliği ve takip süreçleri güçlendirilir.',
            en: 'İlimera PDKS device provides secure and controlled access at door entry-exit points in institutions. Authorized personnel or students can easily enter with card or mobile barcode. Unauthorized access attempts are blocked by the device; audible and visual alerts are activated and notifications are sent to the system administrator. All entries and exits are instantly recorded and detailed reports are created to strengthen institutional security and tracking processes.'
          },
          points: {
            tr: [
              'Kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar.',
              'Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile giriş yapabilir.',
              'Yetkisiz erişim denemeleri cihaz tarafından engellenir.',
              'Sesli ve görsel uyarılar devreye girer.',
              'Sistem yöneticisine bildirim gönderilir.',
              'Tüm giriş-çıkışlar anlık olarak sisteme kaydedilir.',
              'Detaylı raporlar oluşturularak güvenlik ve takip süreçleri güçlendirilir.'
            ],
            en: [
              'Ensures secure and controlled access at door entry-exit points in institutions.',
              'Authorized personnel or students can enter with a card or mobile barcode.',
              'Unauthorized access attempts are blocked by the device.',
              'Audible and visual warnings are activated.',
              'Notifications are sent to the system administrator.',
              'All entries and exits are instantly recorded in the system.',
              'Detailed reports are generated, strengthening security and monitoring processes.'
            ]
          }
        },
        {
          title: {
            tr: 'Yemekhane Kullanımı',
            en: 'Cafeteria Usage'
          },
          description: {
            tr: 'İlimera PDKS cihazı, yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder. Kart veya mobil barkod ile geçiş yapılabilir; sistem yalnızca yetkili kişilerin girişine izin verir. Bu sayede yemek israfı ve yetkisiz kullanım önlenir. Tüm girişler anlık kaydedilir ve yöneticiler detaylı raporlara ulaşabilir. Yemekhane kullanımı böylece düzenli, denetlenebilir ve adil şekilde yönetilir.',
            en: 'İlimera PDKS device quickly and securely checks meal rights of personnel and students at cafeteria entrances. Access can be made with card or mobile barcode; the system only allows entry of authorized persons. This prevents food waste and unauthorized use. All entries are instantly recorded and administrators can access detailed reports. Cafeteria usage is thus managed in an organized, auditable and fair manner.'
          },
          points: {
            tr: [
              'Yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder.',
              'Kart veya mobil barkod ile geçiş imkânı sunar.',
              'Yalnızca yetkili kişilerin girişine izin verir.',
              'Yemek israfı ve yetkisiz kullanımın önüne geçer.',
              'Tüm girişler anlık olarak sisteme kaydedilir.',
              'Yöneticiler detaylı raporlara ulaşabilir.',
              'Yemekhane kullanımı düzenli, denetlenebilir ve adil şekilde yönetilir.'
            ],
            en: [
              'Quickly and securely checks meal rights of personnel and students at cafeteria entrances.',
              'Provides access using cards or mobile barcodes.',
              'Only allows entry of authorized persons.',
              'Prevents food waste and unauthorized use.',
              'All entries are instantly recorded in the system.',
              'Administrators can access detailed reports.',
              'Cafeteria usage is managed in an organized, auditable and fair manner.'
            ]
          }
        },
        {
          title: {
            tr: 'Toplantı Odası Geçiş Kontrolü',
            en: 'Meeting Room Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, toplantı odalarına yalnızca yetkili personelin girişini sağlar. Kart veya mobil barkod ile erişim sağlanır, yetkisiz giriş denemeleri engellenir ve gerekirse alarm veya uyarı gönderilir. Tüm giriş-çıkışlar kaydedilir; hangi personelin hangi saatlerde odayı kullandığı raporlanabilir. Böylece güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.',
            en: 'İlimera PDKS device ensures that only authorized personnel can enter meeting rooms. Access is provided with card or mobile barcode, unauthorized entry attempts are blocked and alarms or warnings are sent if necessary. All entries and exits are recorded; which personnel used the room at what times can be reported. Thus, security is increased and internal resources are managed efficiently.'
          },
          points: {
            tr: [
              'Toplantı odalarına yalnızca yetkili personelin girişini sağlar.',
              'Kart veya mobil barkod ile erişim imkânı sunar.',
              'Yetkisiz giriş denemeleri engellenir.',
              'Gerekirse alarm veya uyarı gönderilir.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Hangi personelin hangi saatlerde odayı kullandığı raporlanabilir.',
              'Güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.'
            ],
            en: [
              'Ensures that only authorized personnel can enter meeting rooms.',
              'Provides access using cards or mobile barcodes.',
              'Unauthorized entry attempts are blocked.',
              'Alarms or warnings are sent if necessary.',
              'All entries and exits are recorded.',
              'Which personnel used the room at what times can be reported.',
              'Security is increased and internal resources are managed efficiently.'
            ]
          }
        },
        {
          title: {
            tr: 'Kütüphane Geçiş Kontrolü',
            en: 'Library Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar. Kart veya mobil barkod ile giriş yapılabilir; yalnızca kayıtlı kullanıcılar erişebilir, yetkisiz girişler engellenir. Kapasite sınırlaması sayesinde kütüphanedeki kişi sayısı kontrol edilir. Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir ve kullanım verimliliği artar. Tüm giriş-çıkışlar kaydedilir ve yöneticilere raporlama imkânı sunulur.',
            en: 'İlimera PDKS device ensures safe and orderly access of members at library entrances. Entry can be made with card or mobile barcode; only registered users can access, unauthorized entries are blocked. The number of people in the library is controlled through capacity limitation. Entry rights can be defined in advance with the reservation feature and usage efficiency increases. All entries and exits are recorded and reporting capability is provided to administrators.'
          },
          points: {
            tr: [
              'Kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar.',
              'Kart veya mobil barkod ile giriş imkânı sunar.',
              'Yalnızca kayıtlı kullanıcılar erişebilir; yetkisiz girişler engellenir.',
              'Kapasite sınırlaması ile kütüphanedeki kişi sayısı kontrol edilir.',
              'Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir, kullanım verimliliği artar.',
              'Tüm giriş-çıkışlar kaydedilir.',
              'Yöneticilere raporlama imkânı sunar.'
            ],
            en: [
              'Ensures safe and orderly access of members at library entrances.',
              'Provides entry using cards or mobile barcodes.',
              'Only registered users can access; unauthorized entries are blocked.',
              'The number of people in the library is controlled through capacity limitation.',
              'Entry rights can be defined in advance with the reservation feature, increasing usage efficiency.',
              'All entries and exits are recorded.',
              'Provides reporting capability to administrators.'
            ]
          }
        },
        {
          title: {
            tr: 'Etkinlik Geçiş Kontrolü',
            en: 'Event Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar. Katılımcılar bilet veya mobil QR kod ile giriş yapabilir; uzun kuyruklar önlenir ve düzenli geçiş sağlanır. Tüm girişler anlık kaydedilir, katılımcı sayısı takip edilir ve kapasite kontrolü yapılır. Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.',
            en: 'İlimera PDKS device provides fast and secure entry at concerts, fairs, seminars and similar events. Participants can enter with tickets or mobile QR codes; long queues are prevented and orderly passage is ensured. All entries are instantly recorded, participant numbers are tracked and capacity control is performed. Security is kept at the highest level by blocking unauthorized entries.'
          },
          points: {
            tr: [
              'Konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar.',
              'Katılımcılar bilet veya mobil QR kod ile giriş yapabilir.',
              'Uzun kuyruklar önlenir ve düzenli geçiş sağlanır.',
              'Tüm girişler anlık kaydedilir.',
              'Katılımcı sayısı takip edilir ve kapasite kontrolü yapılır.',
              'Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.'
            ],
            en: [
              'Provides fast and secure entry at concerts, fairs, seminars and similar events.',
              'Participants can enter with tickets or mobile QR codes.',
              'Long queues are prevented and orderly passage is ensured.',
              'All entries are instantly recorded.',
              'Participant numbers are tracked and capacity control is performed.',
              'Security is kept at the highest level by blocking unauthorized entries.'
            ]
          }
        },
        {
          title: {
            tr: 'Hızlı Kasa Geçiş Kontrolü',
            en: 'Fast Checkout Access Control'
          },
          description: {
            tr: 'İlimera PDKS cihazı, market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar. Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir; yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar. Tüm geçişler kaydedilir, güvenlik ve satış takibi kolaylaşır. Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.',
            en: 'İlimera PDKS device enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas. Access can be made with shopping receipt or mobile QR code; queues are prevented during busy hours and customer satisfaction increases. All passages are recorded, making security and sales tracking easier. Transaction intensity can be analyzed with daily, weekly or monthly reports.'
          },
          points: {
            tr: [
              'Market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar.',
              'Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir.',
              'Yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar.',
              'Tüm geçişler kaydedilir.',
              'Güvenlik ve satış takibi kolaylaşır.',
              'Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.'
            ],
            en: [
              'Enables customers to complete checkout transactions quickly and securely in markets, stores and self-service areas.',
              'Access can be made with shopping receipt or mobile QR code.',
              'Queues are prevented during busy hours and customer satisfaction increases.',
              'All passages are recorded.',
              'Security and sales tracking become easier.',
              'Transaction intensity can be analyzed with daily, weekly or monthly reports.'
            ]
          }
        }
      ],
      technologies: ['RFID', 'QR Code', 'TCP/IP', 'Mobile App', 'Cloud Database']
    },
    'depo-takip-sistemi': {
      title: {
        tr: 'Depo Takip Sistemi',
        en: 'Warehouse Tracking System'
      },
      category: {
        tr: 'Lojistik Çözümleri',
        en: 'Logistics Solutions'
      },
      description: {
        tr: 'Forklift ve depo operasyonları için geliştirilmiş anlık takip sistemi. Sensör tabanlı konum belirleme ve kullanıcı yetkilendirme sistemi ile depo verimliliğini artırır.',
        en: 'Real-time tracking system developed for forklift and warehouse operations. Increases warehouse efficiency with sensor-based location detection and user authorization system.'
      },
      image: 'https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBmb3JrbGlmdCUyMHRyYWNraW5nfGVufDF8fHx8MTc1Njg4MzQwOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'Anlık Forklift Konumu',
          'Çalışma Süresi Takibi',
          'Kullanıcı Kimlik Doğrulama',
          'Rota Optimizasyonu',
          'Performans Analizi',
          'Güvenlik Alarm Sistemi'
        ],
        en: [
          'Real-time Forklift Location',
          'Working Time Tracking',
          'User Authentication',
          'Route Optimization',
          'Performance Analysis',
          'Security Alarm System'
        ]
      },
      specifications: [
        {
          label: { tr: 'Konum Hassasiyeti', en: 'Location Accuracy' },
          value: { tr: '±1 metre', en: '±1 meter' }
        },
        {
          label: { tr: 'Güncelleme Sıklığı', en: 'Update Frequency' },
          value: { tr: '1 saniye', en: '1 second' }
        },
        {
          label: { tr: 'Bağlantı', en: 'Connection' },
          value: { tr: 'Wi-Fi, Bluetooth', en: 'Wi-Fi, Bluetooth' }
        },
        {
          label: { tr: 'Batarya Ömrü', en: 'Battery Life' },
          value: { tr: '8-12 saat', en: '8-12 hours' }
        },
        {
          label: { tr: 'Çalışma Sıcaklığı', en: 'Operating Temperature' },
          value: { tr: '-20°C ile +60°C', en: '-20°C to +60°C' }
        },
        {
          label: { tr: 'Koruma', en: 'Protection' },
          value: { tr: 'IP67', en: 'IP67' }
        }
      ],
      useCases: {
        tr: [
          'Depo Forklift Takibi',
          'Operasyon Verimliliği',
          'Güvenlik Monitoring',
          'Maliyet Optimizasyonu',
          'İş Sağlığı ve Güvenliği'
        ],
        en: [
          'Warehouse Forklift Tracking',
          'Operational Efficiency',
          'Security Monitoring',
          'Cost Optimization',
          'Occupational Health and Safety'
        ]
      },
      technologies: ['GPS/GNSS', 'Wi-Fi', 'IoT Sensors', 'Real-time Analytics']
    },
    'sanal-capa-projesi': {
      title: {
        tr: 'Sanal Çapa Projesi',
        en: 'Virtual Anchor Project'
      },
      category: {
        tr: 'Denizcilik Teknolojisi',
        en: 'Maritime Technology'
      },
      description: {
        tr: 'Denizcilik sektörü için geliştirilen innovatif çözüm. Fiziksel çapaya ihtiyaç olmadan teknenin GPS teknolojisi ile sabit konumda kalmasını sağlar.',
        en: 'Innovative solution developed for the maritime industry. Keeps the boat in a fixed position using GPS technology without the need for a physical anchor.'
      },
      image: 'https://images.unsplash.com/photo-1738339439832-860548f03cc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib2F0JTIwYW5jaG9yJTIwbWFyaW5lJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NTY4ODM0MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: {
        tr: [
          'GPS Tabanlı Sabit Durma',
          'Otomatik Konum Düzeltme',
          'Mobil Uygulama Kontrolü',
          'Kumanda ile Yön Kontrolü',
          'Planlı Rota Sistemi',
          'Acil Durum Modu'
        ],
        en: [
          'GPS-Based Stationary Positioning',
          'Automatic Position Correction',
          'Mobile App Control',
          'Remote Direction Control',
          'Planned Route System',
          'Emergency Mode'
        ]
      },
      specifications: [
        {
          label: { tr: 'GPS Hassasiyeti', en: 'GPS Accuracy' },
          value: { tr: '±2 metre', en: '±2 meters' }
        },
        {
          label: { tr: 'Çalışma Süresi', en: 'Operating Time' },
          value: { tr: '6-8 saat', en: '6-8 hours' }
        },
        {
          label: { tr: 'Kontrol Mesafesi', en: 'Control Range' },
          value: { tr: '500 metre', en: '500 meters' }
        },
        {
          label: { tr: 'Su Geçirmezlik', en: 'Waterproofing' },
          value: { tr: 'IPX8', en: 'IPX8' }
        },
        {
          label: { tr: 'Tekne Boyutu', en: 'Boat Size' },
          value: { tr: '8-15 metre', en: '8-15 meters' }
        },
        {
          label: { tr: 'Deniz Durumu', en: 'Sea Condition' },
          value: { tr: '4 deniz miline kadar', en: 'Up to sea state 4' }
        }
      ],
      useCases: {
        tr: [
          'Tekne Demirleme',
          'Balıkçı Tekneleri',
          'Yat ve Yelkenli Tekneler',
          'Arama Kurtarma',
          'Bilimsel Araştırma Tekneleri'
        ],
        en: [
          'Boat Anchoring',
          'Fishing Boats',
          'Yachts and Sailboats',
          'Search and Rescue',
          'Scientific Research Vessels'
        ]
      },
      technologies: ['GPS/GNSS', 'Marine Electronics', 'Wireless Control', 'Mobile App']
    }
  };

  const project = projects[slug as keyof typeof projects];

  if (!project) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t.projectNotFound}</h1>
        <p className="text-gray-600 mb-8">{t.projectNotFoundDesc}</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            {t.backToProducts}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link to="/urunler" className="flex items-center text-primary hover:text-primary/90 transition-colors">
          <ArrowLeft className="mr-2" size={16} />
          {t.backToProducts}
        </Link>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-muted text-primary">
              {getLocalized(project.category)}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {getLocalized(project.title)}
            </h1>
            {isShalteronModel && (
              <p className="text-lg text-muted-foreground italic font-bold mb-6">
                {t.shalteronSlogan1}
                <br />
                {t.shalteronSlogan2}
              </p>
            )}
            {isShalteronSModel && (
              <p className="text-lg text-muted-foreground italic font-bold mb-6">
                {t.shalteronSSlogan1}
                <br />
                {t.shalteronSSlogan2}
              </p>
            )}
            {slug === 'akilli-gecis-sistemi' && (
              <p className="text-lg text-muted-foreground italic font-bold mb-6">{t.agsSlogan}</p>
            )}
            {slug === 'akilli-gecis-sistemi' ? (
              <AgsHeroNarrative lang={language} />
            ) : (
              <p
                className="text-lg text-muted leading-relaxed mb-8"
                {...(isShalteronFamily
                  ? {
                    lang: language === 'en' ? 'en' : 'tr',
                    style: {
                      textAlign: 'justify' as const,
                      textJustify: 'inter-word' as const,
                      hyphens: 'auto' as const
                    }
                  }
                  : {})}
              >
                {getLocalized(project.description)}
              </p>
            )}
            <div className="flex flex-col space-y-6">

              {slug === 'frigolive' && (
                <Link to="/downloads/frigolive">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}

              {isShalteronFamily && (
                <Link to="/downloads/shalteron">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}

              {slug === 'akilli-gecis-sistemi' && (
                <Link to="/downloads/akilli-gecis-sistemi">
                  <Button size="lg" className="group self-start">
                    {t.exploreMobileApp}
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div className={isShalteronFamily ? 'lg:-ml-4' : ''}>
            <ImageWithFallback
              src={project.image}
              alt={getLocalized(project.title)}
              className={`h-auto rounded-lg shadow-xl ${isShalteronFamily ? 'w-full lg:w-[112%] lg:max-w-none' : 'w-full'}`}
            />
          </div>
        </div>
      </section>

      {/* Features - Shalteron'da gizli; Akıllı Geçiş & Frigolive: dikey liste, Shalteron öne çıkanlarıyla aynı yeşil tik */}
      {!isShalteronFamily && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-6">{t.features}</h2>
          {slug === 'akilli-gecis-sistemi' || slug === 'frigolive' ? (
            <ul className="max-w-3xl space-y-2 list-none pl-0 text-muted-foreground">
              {getLocalized(project.features).map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {getLocalized(project.features).map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center !bg-white dark:!bg-white p-3 rounded-lg text-m"
                >
                  <CheckCircle className="text-primary mr-2 flex-shrink-0" size={18} />
                  <span className="!text-muted dark:!text-white font-medium leading-snug">{feature}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ShalterOn - Akıllı Takip ve Alarm Sistemi (Digital Inputs) devre dışı */}
      {false && slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16"></section>
      )}

      {/* Strategic Highlights (Akıllı Geçiş Sistemi) */}
      {slug === 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 scroll-animate" data-animation="animate-fade-in-up">
          <h2 className="text-3xl font-bold text-foreground mb-8">Güvenlik, Süreklilik ve Uzaktan Kontrol</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Acil Durum Senaryoları ve Güvenlik Entegrasyonu</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Kurumsal güvenlik politikalarının en kritik bileşeni olan acil durum tahliyesi, İlimera AGS ile risk faktörlerinden arındırılmıştır.
                  Yangın veya deprem sensörlerinden alınan sinyallerle eş zamanlı çalışan sistemimiz, manuel müdahaleye gerek kalmaksızın tüm bariyer ve kilit mekanizmalarını otomatik olarak devre dışı bırakır.
                  Bu proaktif yaklaşım, tahliye süreçlerindeki bariyer kaynaklı riskleri minimize ederek mevzuatlara tam uyum sağlar.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-foreground">
                  <CloudOff className="w-5 h-5 text-foreground" />
                  <span className="font-semibold">Kesintisiz Operasyon ve Çevrimdışı Çalışma</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Kurumunuzun güvenliği internet bağlantısına bağımlı değildir. İlimera AGS, geliştirdiğimiz hibrit çalışma teknolojisi sayesinde, ağ kesintilerinde dahi yerel belleğini kullanarak yetkilendirme ve denetim faaliyetlerini kesintisiz sürdürür.
                  Bağlantı sağlandığında gerçekleşen otomatik senkronizasyon ile hiçbir veri kaybı yaşanmaz; güvenlik ve raporlama süreçleriniz %100 süreklilik arz eder.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full border shadow-sm">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 text-emerald-600">
                  <Lock className="w-5 h-5" />
                  <span className="font-semibold">Uzaktan Erişim ve Merkezi Denetim</span>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Mekan ve zamandan bağımsız yönetim imkanı sunan bulut altyapımız, tam kontrolü parmaklarınızın ucuna getirir.
                  Yetkili yöneticiler, web arayüzü veya mobil uygulama üzerinden tüm geçiş noktalarını izleyebilir, anlık müdahalelerde bulunabilir ve yetki matrislerini güncelleyebilirler.
                  Bu yapı, çok şubeli veya geniş yerleşkeli kurumların tek merkezden, efektif ve güvenli bir şekilde yönetilmesini sağlar.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Technical Specifications - Shalteron'da gizli */}
      {slug !== 'akilli-gecis-sistemi' && !isShalteronFamily && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.technicalSpecs}</h2>
          <Card className={slug === 'shalteron' ? 'max-w-[850px]' : ''}>
            <CardContent className={slug === 'shalteron' ? 'p-4' : 'p-6'}>
              {slug === 'frigolive' ? (
                <div className="space-y-6">
                  {/* Bağlantı */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.connection}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(0, 1).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Konum */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.location}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(1, 3).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sensörler */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.sensors}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(3, 6).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sıcaklık ve Nem */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.temperatureHumidity}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(6, 13).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Batarya */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.battery}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(13, 15).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Genel */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">{t.general}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {project.specifications.slice(15).map((spec, index) => (
                        <div key={index} className="flex items-center gap-3 py-1 border-b border-gray-100">
                          <span className="font-medium text-primary whitespace-nowrap">{getLocalized(spec.label)}:</span>
                          <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`grid grid-cols-1 md:grid-cols-2 gap-y-2 ${slug === 'shalteron' ? 'md:gap-x-6' : 'md:gap-x-4'}`}
                >
                  {project.specifications.map((spec, index) => (
                    <div key={index} className="flex items-center gap-2 py-2 border-b border-gray-100">
                      <span className="font-medium text-primary whitespace-nowrap flex-shrink-0">{getLocalized(spec.label)}:</span>
                      <span className="text-muted-foreground">{getLocalized(spec.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Warnings - Shalteron'da gizli */}
      {project.warnings && !isShalteronFamily && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.warningsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getLocalized(project.warnings).map((warning, index) => (
              <Card key={index} className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 rounded-full mt-0.5 mr-3 flex-shrink-0 flex items-center justify-center shadow-md border border-orange-500">
                      <svg className="w-4 h-4 text-orange-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-orange-800 dark:text-orange-200 font-medium">{warning}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Shalteron: ✔ listesi (Kullanım Alanlarından önce) */}
      {isShalteronModel && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <ul className="space-y-2 list-none pl-0 text-muted-foreground">
            {t.shalteronHighlights.map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {isShalteronSModel && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-4">{t.whyShalteronSTitle}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 max-w-3xl">
            <CardContent className="p-6">
              <p className="text-muted-foreground leading-relaxed mb-6">{t.whyShalteronSLead}</p>
              <ul className="space-y-2 list-none pl-0 text-muted-foreground">
                {t.shalteronSHighlightsS.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">{t.useCases}</h2>
        {slug === 'akilli-gecis-sistemi' && project.detailedUseCases ? (
          /* Akıllı Geçiş: Shalteron ile aynı yapı - tek görsel kutusu + başlık/açıklama */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.detailedUseCases.map((item, idx) => (
              <Card key={idx} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 px-4 pt-4 pb-2">
                    <div className="flex-shrink-0 self-center rounded-lg overflow-hidden bg-muted" style={{ width: 150, height: 150 }}>
                      <ImageWithFallback
                        src={AKILLI_GECIS_USE_CASE_IMAGES[idx] ?? AKILLI_GECIS_USE_CASE_IMAGES[0]}
                        alt={getLocalized(item.title)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1 [&>p:last-child]:mb-0">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {getLocalized(item.title)}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {getLocalized(item.description)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : slug === 'frigolive' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {FRIGOLIVE_USE_CASES.map((item, index) => (
              <Card
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white !bg-white dark:!bg-white dark:border-gray-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="relative overflow-hidden">
                    <div className="h-48 w-full bg-muted">
                      <ImageWithFallback
                        src={item.image}
                        alt={getLocalized(item.title)}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    {/* Badge */}
                    <div
                      className={`absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-xl shadow-lg ${item.color}`}
                    >
                      <div className="h-4 w-4 rounded-sm bg-white" />
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 px-6 pt-6 pb-2">
                    <h3 className="text-[20px] font-bold text-gray-900 dark:text-gray-900 tracking-tight mb-1">
                      {getLocalized(item.title)}
                    </h3>
                    <p className="text-[14px] text-gray-900 dark:text-gray-900 leading-relaxed mb-3">
                      {getLocalized(item.description)}
                    </p>
                    <ul className="mt-auto space-y-1">
                      {getLocalized(item.bullets).map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex items-center gap-3 text-[13px] font-medium text-gray-600 dark:text-gray-600">
                          <span className="flex h-1.5 w-1.5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600" aria-hidden />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : isShalteronFamily ? (
          <>
            {isShalteronSModel && (
              <div className="mb-8 max-w-4xl space-y-4 text-muted-foreground leading-relaxed">
                <p>{t.useCasesIntroS1}</p>
                <p>{t.useCasesIntroS2}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(isShalteronSModel ? SHALTERON_S_USE_CASES : SHALTERON_USE_CASES).map((item, index) => (
                <Card key={index} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex items-center gap-4 px-4 pt-4 pb-2">
                      <div className="flex-shrink-0 self-center rounded-lg overflow-hidden bg-muted" style={{ width: 150, height: 150 }}>
                        <ImageWithFallback
                          src={item.image}
                          alt={getLocalized(item.title)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1 [&>p:last-child]:mb-0">
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {getLocalized(item.title)}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {getLocalized(item.description)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getLocalized(project.useCases).map((useCase, index) => {
              const ShalteronIcons = [Sprout, Sprout, Factory, SunMedium, Gauge, BellRing];
              const IconComponent =
                slug === 'shalteron' ? ShalteronIcons[index] ?? Settings : Settings;

              return (
                <Card key={index} className="p-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-foreground font-medium text-m">{useCase}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Ürün Özellikleri (Shalteron) */}
      {isShalteronFamily && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.productFeatures}</h2>
          <Card className="border overflow-hidden max-w-3xl">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <tbody>
                  {(isShalteronSModel ? SHALTERON_S_PRODUCT_FEATURES : SHALTERON_PRODUCT_FEATURES).map((row, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? 'bg-muted/40' : 'bg-background'}
                    >
                      <td className="border-b border-border px-4 py-3 font-medium text-foreground align-top w-1/3">
                        {getLocalized(row.label)}
                      </td>
                      <td className="border-b border-border px-4 py-3 text-muted-foreground">
                        {getLocalized(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <div className="mt-6 max-w-3xl">
            <Card className="bg-primary/5 border-0 shadow-none">
              <CardContent className="p-4">
                <p className="text-m font-bold text-muted-foreground leading-relaxed italic">
                  {t.simCardReminder}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Product Documentation (ShalterOn) */}
      {isShalteronFamily && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">{t.productDocumentation}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8 space-y-6">
              {/* Ürün Kataloğu */}
              {/* Mobil: doküman adı üstte, görüntüle butonu altta */}
              <div className="block md:hidden space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Ürün Kataloğu' : 'Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/shalteron_doc.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white w-full"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
              </div>
              {/* Masaüstü: görüntüle butonu solda, doküman adı sağda */}
              <div className="hidden md:flex md:items-center md:gap-10 lg:gap-12">
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/shalteron_doc.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white flex-shrink-0"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
                <div className="min-w-0 md:pl-2" style={{ marginLeft: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Ürün Kataloğu' : 'Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
              </div>

              {/* Hızlı Kurulum Kılavuzu */}
              {/* Mobil: doküman adı üstte, görüntüle butonu altta */}
              <div className="block md:hidden space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Hızlı Kurulum Kılavuzu' : 'Quick Start Guide'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Cihazı hızlıca devreye almak için adım adım kurulum kılavuzu'
                      : 'Step‑by‑step quick guide to get the device up and running'}
                  </p>
                </div>
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/SHALTERON_hkk_v3.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white w-full"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
              </div>
              {/* Masaüstü: görüntüle butonu solda, doküman adı sağda */}
              <div className="hidden md:flex md:items-center md:gap-10 lg:gap-12">
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/SHALTERON_hkk_v3.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white flex-shrink-0"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
                <div className="min-w-0 md:pl-2" style={{ marginLeft: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Hızlı Kurulum Kılavuzu' : 'Quick Start Guide'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Cihazı hızlıca devreye almak için adım adım kurulum kılavuzu'
                      : 'Step‑by‑step quick guide to get the device up and running'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Product Documentation (Frigolive) — Shalteron ile aynı: solda buton (md+), mobilde üstte metin */}
      {slug === 'frigolive' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">{t.productDocumentation}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8 space-y-6">
              <div className="block md:hidden space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Frigolive Ürün Kataloğu' : 'Frigolive Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
                <Button
                  onClick={() => window.open('/downloads/documents/frigolive_doc.pdf', '_blank', 'noopener,noreferrer')}
                  className="bg-primary hover:bg-primary/90 text-white w-full"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
              </div>
              <div className="hidden md:flex md:items-center md:gap-10 lg:gap-12">
                <Button
                  onClick={() => window.open('/downloads/documents/frigolive_doc.pdf', '_blank', 'noopener,noreferrer')}
                  className="bg-primary hover:bg-primary/90 text-white flex-shrink-0"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
                <div className="min-w-0 md:pl-2" style={{ marginLeft: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Frigolive Ürün Kataloğu' : 'Frigolive Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Product Documentation (Akıllı Geçiş Sistemi) - Shalteron ile aynı yapı */}
      {slug === 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">{t.productDocumentation}</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8 space-y-6">
              {/* Ürün Kataloğu */}
              <div className="block md:hidden space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Ürün Kataloğu' : 'Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/akilli-gecis-sistemi.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white w-full"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
              </div>
              <div className="hidden md:flex md:items-center md:gap-10 lg:gap-12">
                <Button
                  onClick={() =>
                    window.open('/downloads/documents/akilli-gecis-sistemi.pdf', '_blank', 'noopener,noreferrer')
                  }
                  className="bg-primary hover:bg-primary/90 text-white flex-shrink-0"
                >
                  <Download className="mr-2 w-4 h-4" />
                  {t.viewPdf}
                </Button>
                <div className="min-w-0 md:pl-2" style={{ marginLeft: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'tr' ? 'Ürün Kataloğu' : 'Product Catalog'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'tr'
                      ? 'Detaylı ürün bilgileri ve teknik özellikler'
                      : 'Detailed product information and technical specifications'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {slug === 'frigolive' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-6">{t.productVideo}</h2>
          <div className="max-w-2xl">
            <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-0">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={frigoliveVideoUrl}
                    title={t.productVideo}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Technologies - Shalteron, Frigolive ve Akıllı Geçiş Sistemi'nde gizli */}
      {!isShalteronFamily && slug !== 'akilli-gecis-sistemi' && slug !== 'frigolive' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">{t.technologies}</h2>
          <div className="flex flex-wrap gap-3">
            {project.technologies.map((tech) => (
              <Badge key={tech} variant="outline" className="px-4 py-2 text-sm">
                {tech}
              </Badge>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {slug === 'frigolive' && (
              <Button
                size="lg"
                className="group self-start"
                onClick={() => window.open('https://frigolive.com/', '_blank', 'noopener,noreferrer')}
              >
                {t.visitWebsite}
              </Button>
            )}

            <Link to="/iletisim">
              <Button
                size="lg"
                variant="outline"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white"
              >
                {t.contactUs}
              </Button>
            </Link>

            <Link to="/urunler">
              <Button
                variant="outline"
                size="lg"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white">
                {t.viewOtherProjects}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}